/**
 * Generic named plugin registry.
 * Plugins register at module load; catalogs and UI discover them by id.
 */

export type PluginMeta = {
  id: string;
  label: string;
  description?: string;
  /** basic | advanced | lab — UI progressive disclosure */
  tier?: "basic" | "advanced" | "lab";
};

export class Registry<T extends PluginMeta> {
  private readonly map = new Map<string, T>();
  private readonly order: string[] = [];

  register(plugin: T): T {
    if (this.map.has(plugin.id)) {
      throw new Error(`plugin already registered: ${plugin.id}`);
    }
    this.map.set(plugin.id, plugin);
    this.order.push(plugin.id);
    return plugin;
  }

  /** Replace or insert (for hot reload / tests). */
  upsert(plugin: T): T {
    if (!this.map.has(plugin.id)) this.order.push(plugin.id);
    this.map.set(plugin.id, plugin);
    return plugin;
  }

  get(id: string): T {
    const p = this.map.get(id);
    if (!p) {
      const known = [...this.map.keys()].join(", ");
      throw new Error(`unknown plugin "${id}"; known: ${known}`);
    }
    return p;
  }

  tryGet(id: string): T | undefined {
    return this.map.get(id);
  }

  has(id: string): boolean {
    return this.map.has(id);
  }

  list(): T[] {
    return this.order.map((id) => this.map.get(id)!);
  }

  ids(): string[] {
    return [...this.order];
  }

  clear(): void {
    this.map.clear();
    this.order.length = 0;
  }
}
